import { getWallets } from '@wallet-standard/app';
import type { Wallet } from '@wallet-standard/base';

let _solanaUserDisconnected = false;

/** Prefer Phantom’s injected provider when present; otherwise `window.solana` (Backpack, etc.). */
export function getSolanaBrowserProvider(): any {
  const w = window as any;
  const phantomSol = w.phantom?.solana;
  if (phantomSol?.isPhantom) return phantomSol;
  return w.solana ?? phantomSol ?? null;
}

export async function connectSolanaWallet(): Promise<string | null> {
  const provider = getSolanaBrowserProvider();
  if (!provider) {
    window.open('https://phantom.app/', '_blank');
    return null;
  }
  try {
    // Explicit user click: avoid onlyIfTrusted-style silent reconnect where supported.
    let resp: { publicKey?: { toBase58: () => string } } | undefined;
    try {
      resp = await provider.connect({ onlyIfTrusted: false });
    } catch {
      resp = await provider.connect();
    }
    const addr =
      resp?.publicKey?.toBase58() ?? provider.publicKey?.toBase58?.() ?? null;
    if (addr) _solanaUserDisconnected = false;
    return addr;
  } catch {
    return null;
  }
}

export function getSolanaAddress(): string | null {
  // Respect explicit dapp disconnect even if the extension still reports connected (race / partial disconnect).
  if (_solanaUserDisconnected) return null;
  const provider = getSolanaBrowserProvider();
  return provider?.publicKey?.toBase58?.() ?? null;
}

/** Disconnect Phantom-style providers (may be on `solana` or `phantom.solana`). */
export async function disconnectSolanaWallet(): Promise<void> {
  const w = window as any;
  const seen = new Set<object>();
  const providers: unknown[] = [w.solana, w.phantom?.solana].filter(Boolean);
  for (const p of providers) {
    if (!p || typeof p !== 'object' || seen.has(p as object)) continue;
    seen.add(p as object);
    const provider = p as { disconnect?: () => Promise<void> };
    try {
      await provider.disconnect?.();
    } catch {
      /* user rejected or wallet noop */
    }
  }
  _solanaUserDisconnected = true;
}

/** Call from a wallet `connect` event so a session started outside our Connect button still updates state. */
export function markSolanaConnectedIfProviderHasKey(): void {
  const p = getSolanaBrowserProvider();
  if (p?.publicKey) _solanaUserDisconnected = false;
}

/** Phantom (and similar) emit this when the user switches accounts in the extension. */
export function subscribeSolanaAccountChanged(callback: () => void): () => void {
  const provider = getSolanaBrowserProvider();
  if (!provider?.on) return () => {};
  const handler = (publicKey: unknown) => {
    if (publicKey) _solanaUserDisconnected = false;
    callback();
  };
  provider.on('accountChanged', handler);
  return () => provider.removeListener?.('accountChanged', handler);
}

function findSuiWallet(): Wallet | null {
  const { get } = getWallets();
  const all = get();
  for (const w of all) {
    const supportsSui = w.chains?.some((c: string) => c.startsWith('sui:'));
    if (supportsSui) return w;
  }
  return null;
}

let _cachedSuiWallet: Wallet | null = null;
let _cachedSuiAccounts: readonly { address: string }[] = [];
let _suiUserDisconnected = false;

export function getCachedSuiWallet() {
  return _cachedSuiWallet;
}

export async function connectSuiWallet(): Promise<string | null> {
  const wallet = findSuiWallet();
  if (!wallet) {
    window.open('https://slush.app/', '_blank');
    return null;
  }

  const connectFeature = (wallet as any).features?.['standard:connect'];
  if (!connectFeature?.connect) {
    return null;
  }

  try {
    // Wallet Standard: `standard:disconnect` may not revoke authorization; `silent: false` asks for a proper connect flow.
    const result = await connectFeature.connect({ silent: false });
    const accounts = result?.accounts ?? [];
    _cachedSuiWallet = wallet;
    _cachedSuiAccounts = accounts;
    if (accounts.length > 0) {
      _suiUserDisconnected = false;
      return accounts[0].address;
    }
    return null;
  } catch {
    return null;
  }
}

export async function getSuiAddress(): Promise<string | null> {
  if (_suiUserDisconnected) return null;
  if (_cachedSuiAccounts.length > 0) {
    return _cachedSuiAccounts[0].address;
  }
  const wallet = findSuiWallet();
  if (!wallet) return null;

  const accounts = (wallet as any).accounts ?? [];
  if (accounts.length > 0) {
    _cachedSuiWallet = wallet;
    _cachedSuiAccounts = accounts;
    return accounts[0].address;
  }
  return null;
}

/** Revoke dApp connection in the wallet (Wallet Standard) and clear in-memory cache. */
export async function disconnectSuiWallet(): Promise<void> {
  const wallet = _cachedSuiWallet ?? findSuiWallet();
  const disconnectFeature = (wallet as any)?.features?.['standard:disconnect'];
  try {
    await disconnectFeature?.disconnect?.();
  } catch {
    /* ignore */
  }
  _cachedSuiWallet = null;
  _cachedSuiAccounts = [];
  _suiUserDisconnected = true;
}

/** Wallet Standard `change` when authorized accounts update (switch account, new approval, etc.). */
export function subscribeSuiAuthorizedAccountsChange(callback: () => void): () => void {
  const wallet = _cachedSuiWallet;
  if (!wallet) return () => {};
  const events = (wallet as any).features?.['standard:events'];
  if (typeof events?.on !== 'function') return () => {};
  try {
    return events.on('change', (properties: { accounts?: readonly { address: string }[] }) => {
      if (_suiUserDisconnected) return;
      if (properties.accounts !== undefined) {
        _cachedSuiAccounts = properties.accounts;
      }
      callback();
    });
  } catch {
    return () => {};
  }
}

export async function signSuiTransaction(txBytes: Uint8Array): Promise<{ bytes: string; signature: string } | null> {
  if (_suiUserDisconnected) return null;
  const wallet = _cachedSuiWallet ?? findSuiWallet();
  if (!wallet) return null;

  const signFeature = (wallet as any).features?.['sui:signTransaction'];
  if (!signFeature?.signTransaction) {
    const legacySign = (wallet as any).features?.['sui:signTransactionBlock'];
    if (legacySign?.signTransactionBlock) {
      const account = _cachedSuiAccounts[0] ?? (wallet as any).accounts?.[0];
      if (!account) return null;
      return legacySign.signTransactionBlock({ transactionBlock: txBytes, account });
    }
    return null;
  }

  const account = _cachedSuiAccounts[0] ?? (wallet as any).accounts?.[0];
  if (!account) return null;
  return signFeature.signTransaction({ transaction: txBytes, account });
}
